import * as React from 'react';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import { FixMeLater, BaseTransitionProps, reflow, TransitionDuration, ReactElementWithRef } from '../../utils';
import { useForkRef } from '../../hooks';
import { useHoneycombTheme } from '../../theming';

export interface FadeProps extends BaseTransitionProps {
    /**
     * A single child content element.
     */
    children: ReactElementWithRef<Element>;

    /**
     * @ignore
     */
    style?: React.CSSProperties;

    /**
     * The duration for the transition, in milliseconds.
     */
    timeout?: TransitionDuration;
}

/**
 * Fade allows us to fade into a component till it becomes fully visible.
 * It simply shows the component after a certain timeout duration by fading into the component.
 */
export function Fade(props: Readonly<FadeProps>): React.JSX.Element {
    const { theme } = useHoneycombTheme();
    const { duration, easing } = theme.transitions;

    const {
        appear = true,
        children,
        in: inProp,
        onEnter,
        onEntered,
        onEntering,
        onExit,
        onExited,
        onExiting,
        style,
        timeout = {
            enter: duration.enteringScreen,
            exit: duration.leavingScreen,
        },
        ref,
        ...other
    } = props;

    const nodeRef = React.useRef<HTMLElement>(null);
    const handleRefIntermediary = useForkRef(children.ref, nodeRef);
    const handleRef = useForkRef(handleRefIntermediary, ref);

    const handleEnter = (isAppearing: boolean): void => {
        const { current: node } = nodeRef;
        if (!node) return;

        node.style.transition = `opacity ${timeout.enter}ms ${easing.easeInOut}`;
        reflow(node);

        onEnter?.(isAppearing);
    };

    const handleExit = (): void => {
        const { current: node } = nodeRef;
        if (!node) return;

        node.style.transition = `opacity ${timeout.exit}ms ${easing.easeInOut}`;
        onExit?.();
    };

    return (
        <Transition
            nodeRef={nodeRef}
            in={inProp}
            appear={appear}
            timeout={timeout}
            onEnter={handleEnter}
            onEntered={onEntered}
            onEntering={onEntering}
            onExit={handleExit}
            onExited={onExited}
            onExiting={onExiting}
            {...other}
        >
            {(status: TransitionStatus, childProps: (Partial<FixMeLater> & React.Attributes) | undefined) => {
                return React.cloneElement(children, {
                    ref: handleRef,
                    style: {
                        opacity: ['entering', 'entered'].includes(status) ? 1 : 0,
                        visibility: status === 'exited' && !inProp ? 'hidden' : undefined,
                        willChange: 'opacity',
                        ...style,
                        ...children.props.style,
                    },
                    ...childProps,
                });
            }}
        </Transition>
    );
}
